import { PermissionProvider } from 'portal-commons';
import React, { useEffect } from 'react';
import { Route, Switch, useLocation, Redirect } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';
import { SecureRoute } from '@okta/okta-react';
import CustomLoginCallback from './CustomLoginCallback';
import Initializer from './Initializer';
import Protected from './Protected';
import PageNotFound from './404';
import { authRoutes } from './applications/auth/authRoutes';
import cspRoutes from './applications/CspPortal/routes';
import { Home, PrivateRoute, IdleWarningCountdownModal } from './components';
import Permissions from './constants/permissions';
import { useAuthContext } from './contexts';

const AppWithRouterAccess = () => {
  const { user, roles, profile } = useAuthContext();
  const location = useLocation();

  const computeViewName = (routeMatches) => {
    let viewName = '';
    for (let index = 0; index < routeMatches.length; index++) {
      const routeMatch = routeMatches[index];
      const { path } = routeMatch;

      if (!path) {
        return null;
      }

      if (path.startsWith('/')) {
        viewName = path;
      } else {
        viewName += viewName.endsWith('/') ? path : `/${path}`;
      }
    }

    return viewName || '/';
  };

  useEffect(() => {
    const allRoutes = [];
    [...authRoutes, ...cspRoutes].forEach((route) => {
      if (Array.isArray(route.path)) {
        route.path.forEach((path) => {
          allRoutes.push({ ...route, path });
        });
      } else {
        allRoutes.push(route);
      }
    });
    const routeMatches = allRoutes.filter((route) => {
      const regex = new RegExp(route.path.replace(/:[^\/]+/g, '\\w+'));
      return regex.test(location.pathname);
    });
    const viewName = routeMatches && computeViewName(routeMatches);
    if (viewName) {
      datadogRum.startView({ name: viewName });
    }
  }, [location.pathname]);

  return (
    <PermissionProvider permissions={Permissions} roles={roles}>
      <Initializer />
      <IdleWarningCountdownModal />
      <Switch>
        <Route
          key="callback"
          exact
          path="/implicit/callback"
          component={CustomLoginCallback}
        />
        <Route key="home" exact path="/" component={Home} />
        <SecureRoute
          key="protected"
          exact
          path="/protected"
          component={Protected}
        />
        {authRoutes.map((config) => (
          <Route
            key={config.key}
            exact
            path={config.path}
            component={config.component}
          />
        ))}
        {cspRoutes
          .filter(
            (cspRoute) =>
              !cspRoute.countries ||
              cspRoute.countries.includes(profile?.market)
          )
          .map((config) => (
            <PrivateRoute
              key={config.key}
              exact
              path={config.path}
              title={config.title}
              component={config.component}
              roles={config.roles}
            />
          ))}
        <Route path="/404" component={PageNotFound} key="404" />
        <Redirect from="*" to={user ? '/404' : '/login'} />
      </Switch>
    </PermissionProvider>
  );
};

export default AppWithRouterAccess;
